@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.billing.askBillingGuide
import com.royceecoleman.bill.billing.downloadBillingFile
import com.royceecoleman.bill.shared.billing.BillingDownload
import com.royceecoleman.bill.shared.billing.BillingGuide
import com.royceecoleman.bill.shared.billing.BillingWorkspace
import com.royceecoleman.bill.shared.billing.GuideRequest
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

internal fun focusBillingSection(id: String) {
    (document.getElementById(id) as? HTMLElement)?.let {
        it.scrollIntoView()
        it.focus()
    }
}

internal fun focusBillingHeading() {
    (document.querySelector(".bill-main h1") as? HTMLElement)?.let {
        it.setAttribute("tabindex", "-1")
        it.scrollIntoView()
        it.focus()
    }
}

@Composable
internal fun GuideSteps(current: Int) {
    Ol({
        classes("bill-guide-steps")
        attr("aria-label", "Billing workflow progress")
    }) {
        BillingGuide.steps.forEachIndexed { index, label ->
            Li({ if (index + 1 == current) attr("aria-current", "step") }) {
                Span { Text("${index + 1}. $label") }
            }
        }
    }
}

@Composable
internal fun QuickStartGuide(start: () -> Unit) {
    H1 { Text("Your first month with BILL.") }
    P { Text("Five steps, with you in control. Setup is reusable; your next month starts with notes.") }
    GuideSteps(1)
    BillingGuide.help.forEach { (title, explanation) ->
        Div({ classes("bill-card") }) {
            H2 { Text(title) }
            P { Text(explanation) }
        }
    }
    BillButton("Start my billing workflow →", kind = "primary", action = start)
}

@Composable
internal fun AskBillPanel(
    page: String,
    w: BillingWorkspace?,
    dirty: Boolean,
    hasProfiles: Boolean,
    close: () -> Unit,
) {
    var question by remember { mutableStateOf("") }
    var consent by remember { mutableStateOf(false) }
    var sending by remember { mutableStateOf(false) }
    var answer by remember { mutableStateOf("") }
    var error by remember { mutableStateOf("") }
    var remaining by remember { mutableStateOf<Int?>(null) }
    val scope = rememberCoroutineScope()
    LaunchedEffect(Unit) { focusBillingSection("bill-guide-panel") }
    Div({
        classes("bill-card", "bill-guide-panel")
        id("bill-guide-panel")
        attr("tabindex", "-1")
        attr("aria-label", "Ask BILL help")
    }) {
        H2 { Text("Ask BILL") }
        P { Text("Optional AI help, not an approver. This panel cannot change your work or spend drafting credits.") }
        H3 { Text("Your next step · no AI needed") }
        P {
            Text(
                if (page == "review") {
                    BillingGuide.next(w, dirty)
                } else {
                    when (page) {
                        "profile", "profiles" -> "Pair at least two rough notes with accepted entries, then confirm client requirements."
                        "new" -> "Choose a profile and month, add one note per task, then Save notes. No AI call happens when saving."
                        "rules" -> "Write the rules every draft should follow, or ask BILL to change them. Saved rules apply to your next draft."
                        "plan" -> "Review credits here. The price and renewal terms appear at Stripe checkout before subscribing."
                        "home", "help" ->
                            if (hasProfiles) {
                                "Your examples are saved. Start a monthly workspace, or open saved work from Overview & history."
                            } else {
                                BillingGuide
                                    .next(
                                        null,
                                        false,
                                    )
                            }
                        else -> BillingGuide.next(null, false)
                    }
                },
            )
        }
        if (w != null && w.entries.isNotEmpty()) {
            BillButton("Go to review checks") { focusBillingSection("bill-invoice") }
            w.issues.firstOrNull { it.blocking && it.sourceId.isNotBlank() }?.let { issue ->
                BillButton("Go to first entry needing a fix") { focusBillingSection("bill-entry-${issue.sourceId}") }
            }
        }
        BillDetails("Common questions — no AI or credits") {
            BillingGuide.help.forEach { (title, text) ->
                H3 { Text(title) }
                P { Text(text) }
            }
        }
        P({
            classes("bill-muted")
        }) {
            Text(
                "Only your question, this screen and counts from saved progress will be sent to the configured AI provider. No notes, examples, narratives, invoice fields or validation text are attached. Do not include confidential facts in your question. Answers may be wrong.",
            )
        }
        if (dirty) {
            P({
                classes("bill-alert", "bill-notice")
            }) { Text("You have unsaved edits. AI help cannot see them; save if you want its progress counts to be current.") }
        }
        BillArea("Your question for Ask BILL", question, "How do I choose examples? Why can't I export?") {
            if (!sending) {
                question =
                    it.take(1200)
            }
        }
        BillCheck("I agree to send this question and the numeric progress summary to AI", consent) { if (!sending) consent = it }
        BillButton(if (sending) "Asking BILL…" else "Send question to AI", sending || !consent || question.isBlank(), "primary") {
            if (!sending && consent && question.isNotBlank()) {
                sending = true
                error = ""
                answer = ""
                val request = GuideRequest(question, page, w?.id.orEmpty(), consent = true)
                scope.launch {
                    try {
                        val reply = askBillingGuide(request)
                        answer = reply.answer
                        remaining = reply.remaining
                    } catch (
                        cancel: CancellationException,
                    ) {
                        throw cancel
                    } catch (
                        failure: Exception,
                    ) {
                        error = failure.message ?: "AI help is unavailable. Use the quick-start guide."
                    } finally {
                        sending = false
                        consent = false
                    }
                }
            }
        }
        P({
            classes("bill-muted")
        }) {
            Text(
                "AI help has a separate daily question limit, including failed attempts. It does not use drafting credits. No automatic retries. ${remaining?.let {
                    "$it help questions remain today (UTC)."
                }.orEmpty()}",
            )
        }
        if (error.isNotBlank()) {
            P({
                classes("bill-alert")
                attr("role", "alert")
            }) { Text(error) }
        }
        if (answer.isNotBlank()) {
            Div({
                classes("bill-guide-answer")
                attr("role", "status")
            }) {
                H3 { Text("AI guidance · verify before acting") }
                P { Text(answer) }
                P { Text("This answer reflects saved progress when you asked. Use the current Next step and checks for what to do now.") }
            }
        }
        BillButton("Close help & return to work", action = close)
    }
}

/** Entirely local, synthetic and disposable: never enters the user's history or calls any API. */
@Composable
internal fun SampleWalkthrough(start: () -> Unit) {
    var step by remember { mutableStateOf(1) }
    var minutes by remember { mutableStateOf("") }
    var approved by remember { mutableStateOf(false) }
    H1 { Text("Try BILL with a sample.") }
    P({
        classes("bill-alert", "bill-notice")
    }) { Text("SYNTHETIC PRACTICE ONLY · No AI calls, credits, payments or saved client records. Leaving this screen resets the sample.") }
    GuideSteps(step)
    Div({ classes("bill-card") }) {
        H2 { Text(BillingGuide.steps[step - 1]) }
        when (step) {
            1 -> {
                P { Text("Teach the style with paired examples, not a long prompt. These fictional pairs preserve the original facts:") }
                P({ classes("bill-source") }) { Text("rev MSJ re causation → Review motion for summary judgment regarding causation.") }
                P({ classes("bill-source") }) { Text("call client re depo → Confer with client regarding deposition.") }
            }
            2 -> {
                P {
                    Text(
                        "Our fictional client permits L240 / A104 and records work in 6-minute units. In real work, check the client's guidelines; the app's defaults do not establish permission.",
                    )
                }
                P { Text("This example uses an agreed rate of $350/hour. BILL never invents rates or rounds up time.") }
            }
            3 -> {
                P { Text("Fictional record: September 2, 2026; 30 actual minutes; rev MSJ re causation.") }
                P {
                    Text(
                        "Paste one task per line, or date, minutes and note as three tab-separated spreadsheet columns. Save notes stores them without calling AI.",
                    )
                }
                P {
                    Text(
                        "The next screen uses a prewritten sample draft. In real work, Draft with AI requires confirmation and uses one credit per note drafted.",
                    )
                }
            }
            4 -> {
                P({ classes("bill-source") }) { Text("Original: rev MSJ re causation · 2026-09-02 · recorded time: 30 minutes") }
                P { Text("Sample draft: Review motion for summary judgment regarding causation. Codes: L240 / A104.") }
                BillField("Sample actual minutes", minutes, "Use the 30 minutes in the fictional record") {
                    minutes = it
                    approved = false
                }
                if (minutes !=
                    "30"
                ) {
                    P(
                        { classes("bill-issues") },
                    ) { Text("Practice fix: enter the actual 30 minutes from the record, not an estimate.") }
                }
                BillCheck("I checked the sample facts, recorded time and codes", approved) { approved = it && minutes == "30" }
                P { Text("Real work also requires Save edits, resolving blocking checks and explicit approval of every entry.") }
            }
            5 -> {
                P {
                    Text(
                        "30 minutes = 0.5 hours × $350/hour = $175.00. This CSV contains a reviewed sample entry, not the rough note. Never import it as a real invoice.",
                    )
                }
                BillButton("Download sample CSV") {
                    downloadBillingFile(
                        BillingDownload(
                            "BILL-SAMPLE-NOT-FOR-BILLING.csv",
                            "text/csv",
                            "Sample,Date,Narrative,Minutes,Hours,Rate,Amount,Task,Activity\r\nNOT FOR BILLING,2026-09-02,Review motion for summary judgment regarding causation.,30,0.5,350.00,175.00,L240,A104\r\n",
                        ),
                    )
                }
                P {
                    Text(
                        "For your own work, choose CSV with column mapping or LEDES 1998B if supported by your billing system. Your examples and client rules are reusable next month.",
                    )
                }
                BillButton("Start with my own examples →", kind = "primary", action = start)
            }
        }
        Div({ classes("bill-row") }) {
            if (step > 1) BillButton("← Previous sample step") { step-- }
            if (step < 5) BillButton("Next sample step →", step == 4 && !approved, "primary") { step++ }
        }
    }
}
